import React from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components/native';
import { calcWidth, calcHeight, isWeb } from '../../../utils/dimensions';
import { KeyboardAwareView } from '../../../components/shared';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { I18nManager, ScrollView, StyleProp, ViewStyle } from 'react-native';
import useCustomTranslation from '../../../hooks/useCustomTranslation';
import useContactUs from './useContactUs';
import ErrorMessage from '../../../components/shared/ErrorMessage';
import { validateEmail } from '../../../utils/stringValidations';
import Header from '../../../components/Header';
import GenericButton from '../../../components/shared/buttons/GenericButton';
import { IPanelChildInjectedProps } from '../../../store/bottomPanel/bottomPanel';
import { DRAWER_FORM_WIDTH } from '../../../utils/constants/drawer';

interface IProps extends IPanelChildInjectedProps {}

const Contact: React.FC<IProps> = ({ onClosePanel }) => {
  const insets = useSafeAreaInsets();
  const theme = useTheme();
  const { prefixedT, t } = useCustomTranslation('appSettingsStack.contactScreen');
  const {
    contentValue,
    setContentValue,
    emailValue,
    setEmailValue,
    subjectValue,
    setSubjectValue,
    uploadState,
    sendTicket,
  } = useContactUs({
    onTicketSent: onClosePanel,
  });
  const isEmailValid = validateEmail(emailValue);
  const isFormValid = contentValue.length > 6 && subjectValue.length > 2 && isEmailValid;

  return (
    <S.Container>
      <KeyboardAwareView contentContainerStyle={S.KeyboardAwareViewStyle}>
        <ScrollView bounces={false}>
          <S.View style={{ paddingBottom: insets.bottom || 15 }}>
            <S.Main>
              <S.TextsContainer>
                <S.Heading>{prefixedT('heading')}</S.Heading>
                <S.InstructionText>{prefixedT('instructions')}</S.InstructionText>
                {uploadState.error && <ErrorMessage msg={prefixedT('uploadError')} />}
              </S.TextsContainer>
              <S.InputWrapper>
                <S.Input
                  value={subjectValue}
                  onChangeText={setSubjectValue}
                  maxLength={64}
                  placeholder={prefixedT('subjectPlaceholder')}
                  placeholderTextColor={theme.colors.gray19}
                />
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Input
                  value={emailValue}
                  onChangeText={setEmailValue}
                  placeholder={prefixedT('emailPlaceholder')}
                  placeholderTextColor={theme.colors.gray19}
                />
              </S.InputWrapper>
              <S.TextBox
                value={contentValue}
                onChangeText={setContentValue}
                maxLength={1024}
                multiline
                placeholder={prefixedT('textBoxPlaceholder')}
                placeholderTextColor={theme.colors.gray19}
              />

              <S.ContinueButtonWrapper>
                <S.ContinueButton
                  content={t('common.Send')}
                  onPress={sendTicket}
                  disabled={!isFormValid || uploadState.sending}
                  isLoading={uploadState.sending}
                />
              </S.ContinueButtonWrapper>
            </S.Main>
          </S.View>
        </ScrollView>
      </KeyboardAwareView>
    </S.Container>
  );
};

const inputSlateBaseStyle = ({ theme }: { theme: DefaultTheme }) => css`
  width: 100%;
  border-radius: 25px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.lightGray36};
`;

const S = {
  Container: styled.View`
    flex: 1;
    height: 100%;
  `,
  View: styled.View`
    flex: 1;
    margin: auto ${calcWidth(24)}px;
  `,
  Text: styled.Text``,
  Main: styled.View`
    width: ${DRAWER_FORM_WIDTH}px;
    flex: 1;
  `,
  TextsContainer: styled.View`
    align-items: flex-start;
    margin: ${calcHeight(12)}px 0 ${calcHeight(24)}px;
  `,
  Heading: styled.Text`
    ${({ theme }) => css`
      color: ${theme.colors.darkPrimary};
      font-size: ${theme.fontSizes.s26}px;
      font-family: ${theme.fontFamilies.GilroyMedium};
      margin-bottom: ${calcHeight(8)}px;
    `}
  `,
  InstructionText: styled.Text`
    ${({ theme }) => css`
      color: ${theme.colors.gray19};
      font-size: ${theme.fontSizes.s15}px;
      line-height: ${theme.fontSizes.s15 * 1.4}px;
    `}
  `,
  SubjectLabel: styled.Text`
    ${({ theme }) => css`
      color: ${theme.colors.darkPrimary};
      font-size: ${theme.fontSizes.s16}px;
      font-family: ${theme.fontFamilies.GilroyMedium};
    `}
  `,
  ContinueButtonWrapper: styled.View`
    flex: 1;
    justify-content: flex-end;
    margin-top: ${() => (isWeb ? '40px' : '0px')};
    align-items: center;
  `,
  InputWrapper: styled.View`
    ${inputSlateBaseStyle};
    height: ${calcHeight(50)}px;

    align-items: center;
    margin-bottom: ${() => (isWeb ? '24px' : `${calcHeight(24)}px`)};
  `,
  Input: styled.TextInput`
    padding: 0 ${() => (isWeb ? '22px' : `${calcHeight(22)}px`)};
    width: 100%;
    flex: 1;
    ${({ theme }) => css`
      font-size: ${theme.fontSizes.s16}px;
      color: ${theme.colors.text};
    `}
  `,
  TextBox: styled.TextInput`
    ${inputSlateBaseStyle};
    aspect-ratio: ${315 / 138};
    min-height: ${() => (isWeb ? '70px' : 'auto')};
    padding: ${calcWidth(16)}px ${calcWidth(22)}px;
    margin-bottom: ${calcHeight(24)}px;
    text-align-vertical: top;
    ${({ theme }) => css`
      font-size: ${theme.fontSizes.s16}px;
      color: ${theme.colors.text};
    `}
  `,
  Header: styled(Header)`
    background: transparent;
  `,
  ContinueButton: styled(GenericButton)`
    min-width: ${DRAWER_FORM_WIDTH}px;
    height: ${calcHeight(50)}px;
  `,
  ContactHeaderIconStyles: {
    position: 'absolute',
    top: calcHeight(-30),
    right: calcWidth(-30),
    transform: I18nManager.isRTL
      ? [
          {
            scaleX: -1,
          },
        ]
      : undefined,
  } as StyleProp<ViewStyle>,
  KeyboardAwareViewStyle: {
    flex: 1,
  } as StyleProp<ViewStyle>,
};

export default Contact;
